<template>
  <div class="w-100 mt-4 mb-4">
    <!-- Header section -->
    <div class="d-flex flex-column align-items-center mb-5">
      <div class="d-flex align-items-center">
        <h2 class="mb-0">{{ title }}</h2>
        <button
          v-if="isSingleCompany"
          class="btn btn-icon ms-2"
          :class="{
            'btn-secondary': activeTab === 'settings',
            'btn-phoenix-secondary': activeTab !== 'settings',
          }"
          @click="selectTab('settings')"
        >
          <font-awesome-icon :icon="['fas', 'gear']" />
        </button>
        <label
          v-if="isAdmin"
          class="alert alert-subtle-warning text-center admin-extra-info ms-2 mb-0"
        >
          {{ adminId }}
        </label>
      </div>
      <transition name="slide-fade">
        <button
          v-if="devicesTotal > 0"
          type="button"
          class="devices-summary-badge badge badge-phoenix text-secondary border border-translucent bg-white fw-normal px-2 py-1 fs-9 mt-2"
          @click="goToDevicesTab()"
        >
          <FontAwesomeIcon icon="plug" class="me-2" />
          <span
            >{{ formatNumberWithSpaces(devicesTotal) }}
            {{ t("common.devices") }}</span
          >
          <span class="mx-2 text-gray-300">|</span>
          <FontAwesomeIcon icon="bolt" class="me-2" />
          <span class="text-capitalize">
            {{ formatNumberWithSpaces(Math.round(powerTotal * 100) / 100) }}
            {{ t("common.kw") }}</span
          >
        </button>
      </transition>
    </div>

    <!-- Info Blocks Row -->
    <div class="row g-3 mb-4 justify-content-center">
      <template v-for="(block, index) in infoBoxes" :key="block.id">
        <div
          v-if="!block.hidden"
          class="col-4 text-center"
          :class="{ 'border-translucent border-end': index < 2 }"
        >
          <!-- Only a balance owed to the company counts as money pending; anything else reads "-" -->
          <PendingPayoutFigure
            v-if="block.id === 'invoice-history'"
            class="mb-2"
            :parts="pendingPayouts"
            :isLoading="block.isLoading"
          />

          <!-- Value Display -->
          <div
            v-else-if="'displayValue' in block"
            class="d-inline-flex justify-content-center mb-2 align-items-end"
          >
            <div
              v-if="block.isLoading"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span class="visually-hidden">Loading...</span>
            </div>
            <h3 v-else class="mx-2 mb-0 fs-6">{{ block.displayValue }}</h3>
            <div v-if="block.unit" class="fs-9 text-muted">
              {{ $t(block.unit) }}
            </div>
          </div>

          <!-- Label Display -->
          <h4 v-if="'title' in block" class="mb-3 fs-8 font-serif">
            {{ $t(block.title) }}
          </h4>

          <!-- Button Area -->
          <div
            v-if="'buttonText' in block"
            class="d-inline-flex align-items-center justify-content-center"
          >
            <!-- Special handling for Invoice History button when canSubmitInvoice is true -->
            <template
              v-if="block.id === 'invoice-history' && props.canSubmitInvoice"
            >
              <!-- Submit Invoice Button -->
              <button
                class="btn"
                :class="{
                  'btn-secondary':
                    activeTab === block.id &&
                    invoiceManagementActiveTab === 'submit-invoice',
                  'btn-subtle-primary': !(
                    activeTab === block.id &&
                    invoiceManagementActiveTab === 'submit-invoice'
                  ),
                }"
                :disabled="block.disabled"
                @click="selectTab(block.id)"
              >
                <FontAwesomeIcon icon="file-invoice-dollar" class="me-1" />
                {{ $t("dashboard.companyData.submit_invoice") }}
              </button>
              <!-- Invoice History Icon Button -->
              <button
                class="btn btn-icon ms-2"
                :class="{
                  'btn-secondary':
                    activeTab === block.id &&
                    invoiceManagementActiveTab === 'invoice-history',
                  'btn-phoenix-secondary': !(
                    activeTab === block.id &&
                    invoiceManagementActiveTab === 'invoice-history'
                  ),
                }"
                @click="goToInvoiceHistory"
                :title="t('dashboard.companyData.invoice_history')"
              >
                <FontAwesomeIcon icon="folder-open" />
              </button>
            </template>

            <!-- Standard Button Rendering for other cases -->
            <template v-else>
              <button
                class="btn"
                :class="{
                  'btn-secondary': activeTab === block.id,
                  'btn-phoenix-secondary': activeTab !== block.id,
                }"
                :disabled="block.disabled"
                @click="selectTab(block.id)"
              >
                <span v-if="activeTab === block.id">
                  <FontAwesomeIcon icon="caret-down" class="me-1" />
                  {{ $t(block.buttonText) }}
                </span>
                <span v-else>
                  <FontAwesomeIcon :icon="block.icon" class="me-1" />
                  {{ $t(block.buttonText) }}
                </span>
              </button>
              <!-- Income Estimate Icon Button (sits next to Manage Production Strategy) -->
              <button
                v-if="
                  block.id === 'trading-strategy' && props.showIncomeEstimate
                "
                class="btn btn-icon ms-2 btn-phoenix-secondary"
                @click="openIncomeEstimate"
                :title="t('dashboard.income_estimate.view_estimate')"
              >
                <FontAwesomeIcon icon="chart-column" />
              </button>
            </template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import type { PendingPayoutPart } from "@/composables/money/invoiceUtils";
import { useQueryManager } from "@/composables/useQueryManager";
import PendingPayoutFigure from "@/money/PendingPayoutFigure.vue";
import type { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  title: string;
  devicesTotal: number;
  powerTotal: number;
  isAdmin?: boolean;
  showIncomeEstimate?: boolean;
  adminId?: string | number;
  /** Per payout currency, so companies paid out in different currencies are never added together. */
  pendingPayouts: PendingPayoutPart[];
  canSubmitInvoice: boolean;
  certificatesCount: string | number;
  projectedProduction: number | null | undefined;
  isLoading?: boolean;
  isSingleCompany?: boolean;
}>();

const { t } = useI18n();
const { getQueryValue, updateQuery, parsers } = useQueryManager();

const activeTab = getQueryValue<string>(
  "tab",
  parsers.stringWithDefault("certificates")
);

const invoiceManagementActiveTab = getQueryValue<string>(
  "invoice-management-active-tab",
  parsers.stringWithDefault("")
);

interface InfoBox {
  id: string;
  title: string;
  isLoading: boolean;
  /** Absent for the invoice-history box, whose figure PendingPayoutFigure renders. */
  displayValue?: string | number;
  unit?: string;
  buttonText: string;
  icon: IconDefinition | string;
  disabled: boolean;
  queryParams: Record<string, string>;
  hidden?: boolean;
}

interface SettingsBox {
  id: "settings";
  hidden: true;
  queryParams: Record<string, string>;
}

const infoBoxes = computed<(InfoBox | SettingsBox)[]>(() => {
  const boxes: (InfoBox | SettingsBox)[] = [
    {
      id: "invoice-history",
      title: "dashboard.companyData.money_pending",
      isLoading: props.isLoading ?? false,
      buttonText: props.canSubmitInvoice
        ? "dashboard.companyData.submit_invoice"
        : "dashboard.companyData.documents",
      icon: props.canSubmitInvoice ? "file-invoice-dollar" : "folder-open",
      disabled: false,
      queryParams: {
        "invoice-management-active-tab": props.canSubmitInvoice
          ? "submit-invoice"
          : "invoice-history",
      },
    } as InfoBox,
    {
      id: "certificates",
      title: "dashboard.companyData.available_guarantees_of_origin",
      isLoading: props.isLoading ?? false,
      displayValue: props.certificatesCount,
      unit: "common.mwh",
      buttonText: "dashboard.companyData.manage_inventory",
      icon: "warehouse",
      disabled: false,
      queryParams: {
        "certificates-history-active-tab": "overview",
      },
    } as InfoBox,
    {
      id: "trading-strategy",
      title: "dashboard.companyData.projected_production",
      isLoading: props.isLoading ?? false,
      displayValue:
        props.projectedProduction && props.projectedProduction > 0
          ? formatNumberWithSpaces(Math.round(props.projectedProduction))
          : "-",
      unit: "common.mwh",
      buttonText: "dashboard.companyData.manage_production_strategy",
      icon: "chart-line",
      disabled: false,
      queryParams: {
        "trading-strategy-active-tab": "overview",
      },
    } as InfoBox,
    {
      id: "settings",
      hidden: true,
      queryParams: {
        "settings-active-tab": "settings",
      },
    } as SettingsBox,
  ];

  return boxes;
});

function selectTab(tabId: string) {
  const selectedBox = infoBoxes.value.find((box) => box.id === tabId);
  if (!selectedBox) return;

  updateQuery({
    tab: tabId,
    ...(selectedBox.queryParams ?? {}),
  });
}

// Income estimate is a full-page replacement at the DashboardView layer
// (not a tab inside the dashboard) — set `view` and clear `tab`.
function openIncomeEstimate() {
  updateQuery({ view: "income-estimate", tab: undefined });
}

function goToInvoiceHistory() {
  updateQuery({
    tab: "invoice-history",
    "invoice-management-active-tab": "invoice-history",
  });
}

function goToDevicesTab() {
  updateQuery({
    tab: "trading-strategy",
    "production-strategy-active-tab": "devices",
  });
}
</script>

<style scoped lang="scss">
.admin-extra-info {
  padding: 0;
}

.devices-summary-badge {
  cursor: pointer;
  transition:
    background-color 0.15s ease-in-out,
    color 0.15s ease-in-out;
}

.devices-summary-badge:hover {
  background-color: var(--phoenix-secondary-bg) !important;
  color: var(--phoenix-emphasis-color) !important;
}

.devices-summary-badge:focus-visible {
  outline: 2px solid var(--phoenix-primary);
  outline-offset: 2px;
}

/* Slide-fade transition styles */
.slide-fade-enter-active {
  transition: all 0.3s ease-in;
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
